/**
 * phone — the shared phone-number helpers (Design Pass 3, founder item:
 * "make the phone a validated field... turns up with the correct format.
 * enable international numbers").
 *
 * No dependency (boring filter): US numbers are the default and format
 * as PHONE_EXAMPLE, (555) 555-0123; a leading "+" switches to a lenient international
 * treatment (E.164 length bounds, digits grouped for readability — real
 * per-country grouping needs a library and is deliberately out of scope).
 *
 * Three layers, used together by the phone inputs (contact dialog,
 * provider dialog, demographics):
 *   - formatPhoneInput  — as-you-type display formatting;
 *   - phoneProblem      — on-blur validation; friendly, fix-forward copy
 *                         (the message hands over the fix, never scolds);
 *   - normalizePhone    — the STORED string: bare digits for US numbers,
 *                         "+" + digits for international. The wire/DB
 *                         column is plain text; storing normalized keeps
 *                         every read surface formattable.
 *   - formatPhoneDisplay — read-side prettifier for stored values; a
 *                         string it can't confidently parse (letters,
 *                         extensions, legacy punctuation) passes through
 *                         UNCHANGED — never mangle what a human wrote.
 *
 * PHONE_EXAMPLE is the one phone example the product shows: every phone
 * placeholder reads it. It is invented, from the
 * 555-0100 to 555-0199 range set aside for fiction; no real number, and
 * no real area code, is an example anywhere in the product.
 */

/** The one invented phone example, shown as every placeholder. */
export const PHONE_EXAMPLE = '(555) 555-0123';


/** True when the string carries anything beyond digits + phone punctuation
 *  (letters, "ext", etc.) — such values bypass formatting AND validation:
 *  they are accepted as written (legacy data and extensions are real). */
export function isFreeformPhone(raw: string): boolean {
  return /[^\d()+\-.\s]/.test(raw);
}

/** Digits only (the US storage/comparison form). */
function digitsOf(raw: string): string {
  return raw.replace(/\D/g, '');
}

/** A US digit string with the redundant leading trunk "1" dropped. */
function usDigits(raw: string): string {
  const d = digitsOf(raw);
  return d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
}

/**
 * The normalized STORAGE form: "+" + digits when the user led with "+"
 * (international), bare digits otherwise (US). Freeform strings pass
 * through trimmed — never destroy an extension or a written note.
 */
export function normalizePhone(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (isFreeformPhone(trimmed)) return trimmed;
  if (trimmed.startsWith('+')) return `+${digitsOf(trimmed)}`;
  return usDigits(trimmed);
}

/** Progressive US formatting: "(555", "(555) 555", "(555) 555-0123". */
function formatUsProgressive(digits: string): string {
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}${digits.slice(10)}`;
}

/** Lenient international grouping: "+" then digit groups of three (the
 *  readable fallback when the real country grouping is unknown). */
function formatIntl(digits: string): string {
  const groups = digits.match(/\d{1,3}/g) ?? [];
  return `+${groups.join(' ')}`.trimEnd();
}

/**
 * As-you-type formatting. US default; a leading "+" switches to the
 * lenient international grouping. Freeform input (letters/extensions)
 * returns unchanged — the field must not fight the typist.
 */
export function formatPhoneInput(raw: string): string {
  if (raw.trim() === '') return raw.trim() === raw ? raw : raw.trim();
  if (isFreeformPhone(raw)) return raw;
  if (raw.trimStart().startsWith('+')) {
    const digits = digitsOf(raw);
    return digits === '' ? '+' : formatIntl(digits);
  }
  return formatUsProgressive(usDigits(raw));
}

/**
 * On-blur validation. Returns null (fine) or a friendly, fix-forward
 * message. Empty and freeform values are always accepted.
 */
export function phoneProblem(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '' || isFreeformPhone(trimmed)) return null;
  if (trimmed.startsWith('+')) {
    const n = digitsOf(trimmed).length;
    if (n < 8) {
      return 'This international number looks short. After the +, it needs the country code and the full number (8 to 15 digits).';
    }
    if (n > 15) {
      return 'This international number looks long. After the +, phone numbers run at most 15 digits.';
    }
    return null;
  }
  const n = usDigits(trimmed).length;
  if (n < 10) {
    return `This number looks short. A US number has 10 digits, with the area code first: ${PHONE_EXAMPLE}.`;
  }
  if (n > 10) {
    return `This number looks long, since a US number is 10 digits: ${PHONE_EXAMPLE}. For a number outside the US, start it with +.`;
  }
  return null;
}

/**
 * Read-side display formatting for a stored value: a clean US number
 * renders (555) 555-0123; a "+" number renders in the lenient groups;
 * anything else (freeform, wrong lengths) passes through unchanged.
 */
export function formatPhoneDisplay(stored: string | null | undefined): string {
  if (stored === null || stored === undefined) return '';
  const trimmed = stored.trim();
  if (trimmed === '' || isFreeformPhone(trimmed)) return trimmed;
  if (trimmed.startsWith('+')) {
    const digits = digitsOf(trimmed);
    return digits.length >= 8 && digits.length <= 15 ? formatIntl(digits) : trimmed;
  }
  const digits = usDigits(trimmed);
  if (digits.length !== 10) return trimmed;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
